import { buscarBookPorId } from '../../services/api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Flex, Spinner, Text, useToast } from '@chakra-ui/react';
import { MdOutlineLibraryBooks } from 'react-icons/md';
import useAuth from '../../hooks/useAuth';
import { useParams, useSearchParams } from 'react-router-dom';
import PdfBoard from '../../components/PdfBoard';

export type Lesson = {
	id:string;
	nome:string;
	canvaUrl:string;
	bookId:string;
	thumbnail:string;
}

export type Aula = {
	id: string;
    titulo: string;
    alunoId: string;
    materialId: string;
    professorId: string;
    status: string;
}

export type Book = {
	id: string,
	nome: string,
  	descricao: string,
  	capa: string,
  	idioma: string,
  	nivel: string,
	lessons: Lesson[]
}

export default function MateriaisIdBook() {
	const toast = useToast();
	const [ denied, setDenied ] = useState(false);
	const [ loading, setLoading ] = useState(true);
	const [ countdown, setCountdown ] = useState(3600);

	const [ , setBook ] = useState<Book>();
	const [ lessons, setLessons] = useState<Lesson[]>([]);
	const [ lesson, setLesson ] = useState<Lesson>();
	const {user} = useAuth();
	const { bookId } = useParams();
	const [ searchParams ] = useSearchParams();
	const lessonIdInicial = searchParams.get('lesson');
	const lessonInicialAplicada = useRef(false);

	const [ documentloading, setDocumentLoading ] = useState(false);
	const [ numPages, setNumPages ] = useState(0);
	const [ currentPage, setCurrentPage ] = useState(1);

	const buscarBook = useCallback(()=>{
		buscarBookPorId(`${bookId}`).then((response)=>{
			setLoading(false);
			// A API responde 200 com corpo vazio quando o book nao existe, entao
			// `response.data` pode nem ser um objeto - sem esse fallback a lista de
			// lessons virava undefined e o `.sort()` da renderizacao derrubava a janela.
			const book: Book | undefined = response.data || undefined;
			setBook(book);
			setLessons(book?.lessons ?? []);
		}).catch((err)=>{
			toast({
				title: `${err}`,
				status: 'error',
				isClosable: true
			});
		});
	},[ bookId, toast ]);

	useEffect(()=>{
		buscarBook();
	},[ buscarBook ]);

	useEffect(
		() => {
			const intervalId = setInterval(() => {
				if (countdown < 0) {
					setCountdown(0);
					setDenied(true);
					window.close();
				} else {
					setCountdown((countdown) => countdown - 1);
				}
			}, 1000);

			return () => clearInterval(intervalId);
		},
		[ countdown ]
	);

	const handleSetLesson = (id:string) =>{
		setDocumentLoading(true);
		const lessonFilter = lessons.find(item => item.id === id);
		setLesson(lessonFilter);
	}

	// Quando a janela e aberta a partir do card de uma lesson, o PDF dela abre direto:
	// sem isso a janela caia sempre na lista de lessons, que e a mesma tela de onde o
	// usuario acabou de clicar. So vale para a primeira carga, senao o botao de voltar
	// para a lista reabriria a lesson inicial no render seguinte.
	useEffect(() => {
		if (lessonInicialAplicada.current || !lessonIdInicial || lessons.length === 0) return;
		lessonInicialAplicada.current = true;

		const lessonInicial = lessons.find((item) => item.id === lessonIdInicial);
		if (!lessonInicial) return;

		setDocumentLoading(true);
		setLesson(lessonInicial);
	}, [ lessonIdInicial, lessons ]);

	useEffect(()=>{
		let fecharJanela: ReturnType<typeof setTimeout>;
		if(user?.userType === "PROFESSOR"){
			fecharJanela = setTimeout(() => {
				window.close();
			}, 6000 * 20);
		}
		const avisoCopyright = setInterval(() => {
			toast({
				title: `Information`,
				description:'You can access this material by contacting Windfall management. Copyrighted material!',
				status: 'info',
				isClosable: true,
				position:'top-left'
			});
		}, 600000);
		// Sem o cleanup o interval continuava disparando o toast apos sair da pagina.
		return () => {
			clearTimeout(fecharJanela);
			clearInterval(avisoCopyright);
		};
	},[ toast, user?.userType ]);
	
	//@ts-ignore
	function onDocumentLoadSuccess({ numPages }) {
		setNumPages(numPages);
		setTimeout(()=>{
			setDocumentLoading(false);
		}, 2000)
	}
	
	const handlePrev = () => {
		if (currentPage > 1) setCurrentPage(currentPage - 1);
	};

	const handleNext = () => {
		if (currentPage < numPages) setCurrentPage(currentPage + 1);
	};

	return (
		<div>
			{loading ? 
				<Flex 
					flexDir="column" 
					backgroundColor="gray.800" 
					alignItems="center" 
					justifyContent="center"
					width="100%"
					height="100vh"
					gap="20px"
					overflow="hidden"
				>
					<Text fontSize="24px" fontWeight="bold" color="whiteAlpha.900">
						Iniciando...
					</Text>
					<Spinner
						thickness="4px"
						speed="0.65s"
						emptyColor="gray.200"
						color="blue.500"
						size="xl"
					/>
				</Flex>
				:
				<Flex flexDir="column" backgroundColor="gray.800" overflow="hidden">
					<Flex overflow="hidden">
						<Flex
							alignItems={'center'}
							gap={'50px'}
							bgColor={'gray.800'}
							width={'100%'}
							height={'60px'}
							padding="8px"
							position="fixed"
							bottom="0"
							zIndex={999}
							overflow="hidden"
						>
							<Button
									aria-label="Toggle Color Mode"
									onClick={()=>{setLesson(undefined)}}
									_focus={{ boxShadow: 'none' }}
									w="fit-content"
								>
								 <MdOutlineLibraryBooks />
							</Button>
							<Text fontSize={20} color="whiteAlpha.900" fontWeight="extrabold">{user?.name}</Text>
							<Button
								aria-label="Toggle Color Mode"
								onClick={handlePrev}
								_focus={{ boxShadow: 'none' }}
								w="fit-content"
								colorScheme="gray"
								zIndex={999}
								id="btn"
							>
								{'<'}
							</Button>
							<Text fontWeight={900} color={'white'}>
								{`${currentPage}/${numPages}`}
							</Text>
							<Button
								aria-label="Toggle Color Mode"
								onClick={handleNext}
								_focus={{ boxShadow: 'none' }}
								w="fit-content"
								colorScheme="gray"
								zIndex={999}
								id="btn"
							>
								{'>'}
							</Button>
						</Flex>
					</Flex>
					<PdfBoard
						file={lesson?.canvaUrl}
						pageNumber={currentPage}
						onDocumentLoadSuccess={onDocumentLoadSuccess}
						documentKey={lesson?.id}
					/>
					<Flex
						position="absolute"
						height="100vh"
						width="100vw"
						flexDir="column"
						alignItems="center"
						justifyContent="center"
						background="blue.900"
						opacity="0.95"
						backdropFilter="blur(10px)"
						visibility={!denied ? 'hidden' : 'visible'}
						zIndex={999}
						overflow="hidden"
					>
						<h1 style={{
							fontSize: '3rem',
							padding: '10px',
							color: 'white',
							fontWeight: '900',
						}}>Cuidado!</h1>
						<h5 style={{
							fontSize: '1rem',
							textAlign: 'center',
							width: '60%',
							color: 'white',
							marginBottom: '20px',
						}}>
							Não utilize este material de forma inapropriada ou sem consentimento da coordenação da Windfall.
						</h5>
						<Text color="white" marginBottom="20px" fontSize="12px">
							Conteúdo protegido por direitos autorais.
						</Text>
						<Button
							colorScheme="blue"
							onClick={() => {
								setDenied(false);
							}}
						>
							VOLTAR
						</Button>
					</Flex>
					<Flex
						position="absolute"
						height="100vh"
						width="100vw"
						flexDir="column"
						alignItems="center"
						justifyContent="center"
						background="blue.900"
						opacity="1"
						backdropFilter="blur(10px)"
						visibility={!documentloading ? 'hidden' : 'visible'}
						zIndex={999}
					>
						<Text fontSize="24px" fontWeight="bold" color="whiteAlpha.900">
						Carregando...
						</Text>
						<Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
					</Flex>
					<Flex
						position="absolute"
						height="100vh"
						width="100vw"
						background="blue.900"
						backdropFilter="blur(10px)"
						opacity="1"
						justifyContent="center"
						alignItems="center"
						flexWrap="wrap"
						padding="50px"
						paddingBottom="80px"
						overflowY="scroll"
						zIndex={889}
						visibility={lesson ? 'hidden' : 'visible'}
					>
						<Flex
							justifyContent="center"
							alignItems="center"
							flexWrap="wrap"
							gap="10px"
						>
						{
						lessons.length === 0 &&
							<Text color="whiteAlpha.900" fontSize="20px" fontWeight="bold" textAlign="center">
								Nenhuma lesson disponível neste material.
							</Text>
						}
						{
						lessons
							.sort((a, b) => {
								if (Number(a.nome.split(' ')[1]) < Number(b.nome.split(' ')[1])) return -1;
								if (Number(a.nome.split(' ')[1]) > Number(b.nome.split(' ')[1])) return 1;
								return 0;
							})
							.map((lesson, index) => (
								<Flex
									position="relative"
									width="260px"
									height="100px"
									bgColor="blue.700"
									borderRadius="15px"
									alignItems="center"
									padding="10px"
									gap="10px"
									key={index}
									boxShadow="lg"
									_hover={{
										cursor:'pointer',
										bgColor:'blue.600'
									}}
									onClick={()=>{
										handleSetLesson(lesson.id)
									}}
								>
									<Flex
										height="80px"
										bgImage={
											lesson.thumbnail
										}
										minWidth="80px"
										maxWidth="80px"
										bgPosition="center"
										bgSize="cover"
										borderRadius="15px"
									/>
									<Flex flexDir="column">
											<Flex alignItems="center" gap="10px">
												<Text 
													color="white" 
													fontSize="20px" 
													fontWeight="800" 
													textAlign="center"
												>
													{lesson.nome}
												</Text>
											</Flex>
										</Flex>
								</Flex>
							))
							}
						</Flex>
					</Flex>
				</Flex>
			}
		</div>
		
	);
}
